import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { getLanguage, t } from '../i18n';
import './MonthNavigation.css';

/**
 * MonthNavigation provides lightweight previous/next controls with an optional
 * year and month picker.
 */
function MonthNavigation({ year, month, onPrevious, onNext, onYearChange, onMonthChange, today: accountToday }) {
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const navigationRef = useRef(null);
  const pickerTitleId = 'month-picker-title';

  const today = accountToday ? new Date(`${accountToday}T12:00:00`) : new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const isCurrentMonth = year === currentYear && month === currentMonth;

  const yearOptions = useMemo(() => (
    Array.from({ length: 11 }, (_, index) => currentYear - 5 + index)
  ), [currentYear]);

  const locale = getLanguage() === 'ko' ? 'ko-KR' : 'en-US';
  const monthName = new Date(year, month - 1, 1).toLocaleDateString(locale, { month: 'long' });
  const monthOptions = useMemo(() => (
    Array.from({ length: 12 }, (_, index) => {
      const value = index + 1;
      const date = new Date(year, index, 1);
      return {
        value,
        shortName: date.toLocaleDateString(locale, { month: 'short' }),
        fullName: date.toLocaleDateString(locale, { month: 'long' }),
      };
    })
  ), [locale, year]);

  useEffect(() => {
    if (!isPickerOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (navigationRef.current && !navigationRef.current.contains(event.target)) {
        setIsPickerOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsPickerOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPickerOpen]);

  const handleYearChange = (event) => {
    onYearChange(parseInt(event.target.value, 10));
  };

  const handleMonthSelect = (nextMonth) => {
    if (onMonthChange) {
      onMonthChange(nextMonth, year);
    }
    setIsPickerOpen(false);
  };

  return (
    <div className="month-navigation" ref={navigationRef}>
      <div className="month-picker-anchor">
        <button
          type="button"
          className="month-picker-toggle"
          onClick={() => setIsPickerOpen((open) => !open)}
          aria-haspopup="dialog"
          aria-expanded={isPickerOpen}
          aria-controls="month-picker-popover"
          aria-label={t('accountActivity.calendar.monthPicker')}
          title={t('accountActivity.calendar.monthPicker')}
        >
          <CalendarDays size={18} aria-hidden="true" />
        </button>

        <div
          id="month-picker-popover"
          className={`month-picker-popover${isPickerOpen ? ' is-open' : ''}`}
          role={isPickerOpen ? 'dialog' : undefined}
          aria-hidden={!isPickerOpen}
          aria-modal="false"
          aria-labelledby={pickerTitleId}
        >
          <div className="month-picker-header">
            <h2 id={pickerTitleId}>{t('accountActivity.calendar.monthPickerTitle')}</h2>
            <button
              type="button"
              className="month-picker-close"
              onClick={() => setIsPickerOpen(false)}
              aria-label={t('accountActivity.calendar.closeMonthPicker')}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>

          <label className="month-picker-year-label" htmlFor="month-picker-year">
            {t('accountActivity.calendar.selectYear')}
          </label>
          <select
            id="month-picker-year"
            className="year-selector"
            value={year}
            onChange={handleYearChange}
            aria-label={t('accountActivity.calendar.selectYear')}
          >
            {yearOptions.map((optionYear) => (
              <option key={optionYear} value={optionYear}>{optionYear}</option>
            ))}
          </select>

          <div className="month-picker-grid" aria-label={t('accountActivity.calendar.monthPickerTitle')}>
            {monthOptions.map((option) => (
              <button
                type="button"
                key={option.value}
                className={`month-option${option.value === month ? ' is-selected' : ''}`}
                onClick={() => handleMonthSelect(option.value)}
                aria-label={option.fullName}
                aria-current={option.value === month ? 'date' : undefined}
              >
                {locale === 'ko-KR' ? `${option.value}월` : (option.shortName === option.fullName ? option.value : option.shortName)}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="month-display" aria-live="polite">
        <span className={`month-name ${isCurrentMonth ? 'current-month' : ''}`}>
          {monthName}
        </span>
        <span className="month-year-label">{year}</span>
      </div>
      <div className="month-step-controls">
        <button
          type="button"
          className="nav-button nav-button-prev"
          onClick={onPrevious}
          aria-label={t('accountActivity.calendar.previousMonth')}
          title={t('accountActivity.calendar.previousMonth')}
        >
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="nav-button nav-button-next"
          onClick={onNext}
          aria-label={t('accountActivity.calendar.nextMonth')}
          title={t('accountActivity.calendar.nextMonth')}
        >
          <ChevronRight size={20} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default MonthNavigation;
